/**
 * Carousel class provides functionality for a draggable, scrollable carousel 
 * component with support for smooth scrolling and resizing.
 */
export class CarouselV2 {
    private dotnetRef: any; // Reference to the .NET object for invoking .NET methods
    private elementRef: HTMLElement; // Reference to the root element of the carousel
    private itemsContainerRef: HTMLElement; // Reference to the container holding carousel items
    private isDragging: boolean = false; // Indicates if the carousel is in dragging mode
    private startX: number = 0; // Starting X coordinate for dragging
    private scrollLeft: number = 0; // Scroll position before dragging starts
    private resizeObserver: ResizeObserver; // Observer to handle carousel resizing
    private scrollTimeout: number | null = null; // Timeout for debouncing scroll events
    private isScrolling: boolean = false; // Indicates if the carousel is currently scrolling
    private isPointerDown: boolean = false;
    private startClientX: number = 0;
    private readonly dragThresholdPx: number = 10;
    private readonly draggingClass = `carousel-container-v2__items--dragging`;
    private readonly itemClonedClass = `carousel-container-v2__item-wrapper--cloned`;
    private readonly itemClonedSelector = `.${this.itemClonedClass}`;
    private readonly itemWrapperSelector = `.carousel-container-v2__item-wrapper:not(${this.itemClonedSelector})`;
    private boundOnMouseDown: (e: MouseEvent) => void;
    private boundOnTouchStart: (e: TouchEvent) => void;
    private boundOnScroll: (e: Event) => void;
    private boundOnWheel: (e: WheelEvent) => void;
    private boundOnMouseUp: () => void;
    private boundOnMouseMove: (e: MouseEvent) => void;
    private boundOnTouchEnd: () => void;
    private boundOnTouchMove: (e: TouchEvent) => void;
    private boundOnResize: () => void;
    
    /**
     * Initializes a new instance of the Carousel class.
     * Sets up event listeners for drag and scroll handling.
     *
     * @param dotNetRef - Reference to the .NET object
     * @param elementRef - Root HTML element of the carousel
     */
    constructor(dotNetRef: any, elementRef: HTMLElement) {
        this.dotnetRef = dotNetRef;
        this.elementRef = elementRef;
        this.itemsContainerRef = this.elementRef.querySelector('.carousel-container-v2__items') as HTMLElement;

        // Cache bound methods for better performance
        this.boundOnMouseDown = this.OnMouseDown.bind(this);
        this.boundOnTouchStart = this.OnTouchStart.bind(this);
        this.boundOnScroll = this.OnScroll.bind(this);
        this.boundOnWheel = this.OnWheel.bind(this);
        this.boundOnMouseUp = this.OnMouseUp.bind(this);
        this.boundOnMouseMove = this.OnMouseMove.bind(this);
        this.boundOnTouchEnd = this.OnTouchEnd.bind(this);
        this.boundOnTouchMove = this.OnTouchMove.bind(this);
        this.boundOnResize = this.OnResize.bind(this);

        this.Init();
        this.setupEventListeners();
    }

    /**
     * Sets up all event listeners for the carousel.
     */
    private setupEventListeners() {
        // Mouse and touch events for dragging
        this.itemsContainerRef.addEventListener('mousedown', this.boundOnMouseDown);
        this.itemsContainerRef.addEventListener('touchstart', this.boundOnTouchStart);

        // Scroll events with debouncing
        this.itemsContainerRef.addEventListener('scroll', this.boundOnScroll, { passive: true });

        // Wheel events for scroll wheel detection
        this.itemsContainerRef.addEventListener('wheel', this.boundOnWheel, { passive: true });

        // Resize observer
        this.resizeObserver = new ResizeObserver(this.boundOnResize);
        this.resizeObserver.observe(this.itemsContainerRef);
    }

    /**
     * Finds the appropriate element to scroll to based on the index.
     * Returns a clone for out-of-bounds indices, or the regular item for in-bounds indices.
     *
     * @param index - Index of the item to find
     * @returns The target element to scroll to
     */
    private FindScrollTarget(index: number): HTMLElement | null {
        const nonClonedItems = this.itemsContainerRef.querySelectorAll(this.itemWrapperSelector);
        const numberOfItems = nonClonedItems.length;
        
        // If index is within bounds, return the regular item
        if (index >= 0 && index < numberOfItems) {
            return nonClonedItems.item(index) as HTMLElement;
        }
        
        // For out-of-bounds indices, find the corresponding clone
        const allItems = Array.from(this.itemsContainerRef.children) as HTMLElement[];
        const normalizedIndex = ((index % numberOfItems) + numberOfItems) % numberOfItems;
        
        // Find the index of the normalized non-cloned item in the full list
        const normalizedNonClonedElement = nonClonedItems.item(normalizedIndex) as HTMLElement;
        const normalizedElementIndex = allItems.indexOf(normalizedNonClonedElement);
        
        // Calculate the target index by adding the difference
        const targetIndex = normalizedElementIndex + (index - normalizedIndex);
        
        if (targetIndex >= 0 && targetIndex < allItems.length) {
            return allItems[targetIndex];
        }
        
        return normalizedNonClonedElement;
    }

    /**
     * Scrolls to a specified item in the carousel.
     *
     * @param index - Index of the item to scroll to
     * @param behavior - Scrolling behavior ('smooth' or 'instant')
     */
    public MoveTo(index: number, behavior: ScrollBehavior = 'smooth') {
        const targetElement = this.FindScrollTarget(index);
        
        if (targetElement === null || targetElement === undefined) {
            return;
        }

        // Set scrolling flag to prevent interference during programmatic scrolling
        this.isScrolling = true;
        const transitionDelay = behavior === 'smooth' ? 500 : 0;
        
        this.itemsContainerRef.scrollTo({
            left: targetElement.offsetLeft,
            behavior: behavior
        });

        if (targetElement.classList.contains(this.itemClonedClass) === true) {
            const nonClonedItems = this.itemsContainerRef.querySelectorAll(this.itemWrapperSelector);
            const nonCloneIndex = ((index % nonClonedItems.length) + nonClonedItems.length) % nonClonedItems.length;
            
            setTimeout(() => {
                this.TransitionToNonClonedItem(nonCloneIndex);
            }, transitionDelay);
        } else {
            setTimeout(() => {
                this.isScrolling = false;
            }, transitionDelay);
        }
    }

    /**
     * Initializes the carousel by setting up dimensions and positioning.
     */
    private Init() {
        this.OnResize();
    }

    /**
     * Handles the mousedown event for initiating dragging.
     *
     * @param e - Mouse event
     */
    private OnMouseDown(e: MouseEvent) {
        if (e.button !== 0) {
            return;
        }

        this.setupDragListeners();

        this.isPointerDown = true;
        this.isDragging = false;

        this.startClientX = e.clientX;
        this.startX = e.pageX - this.itemsContainerRef.offsetLeft;
        this.scrollLeft = this.itemsContainerRef.scrollLeft;
    }

    /**
     * Handles the touchstart event for initiating dragging.
     *
     * @param e - Touch event
     */
    private OnTouchStart(e: TouchEvent) {
        if (e.touches.length === 0) {
            return;
        }

        this.setupTouchListeners();

        this.isPointerDown = true;
        this.isDragging = false;

        this.startClientX = e.touches[0].clientX;
        this.startX = e.touches[0].pageX - this.itemsContainerRef.offsetLeft;
        this.scrollLeft = this.itemsContainerRef.scrollLeft;
    }

    /**
     * Sets up mouse event listeners for dragging.
     */
    private setupDragListeners() {
        window.addEventListener('mouseup', this.boundOnMouseUp);
        window.addEventListener('mousemove', this.boundOnMouseMove);
    }

    /**
     * Sets up touch event listeners for dragging.
     */
    private setupTouchListeners() {
        window.addEventListener('touchend', this.boundOnTouchEnd);
        window.addEventListener('touchmove', this.boundOnTouchMove);
    }

    /**
     * Starts the dragging process.
     *
     * @param pageX - X coordinate of the pointer
     */
    private startDragging(pageX: number) {
        this.isDragging = true;
        this.startX = pageX - this.itemsContainerRef.offsetLeft;
        this.scrollLeft = this.itemsContainerRef.scrollLeft;
        this.itemsContainerRef.classList.add(this.draggingClass);
    }

    /**
     * Handles the mouseup event for stopping dragging.
     */
    private OnMouseUp() {
        this.removeDragListeners();
        this.isPointerDown = false;
        this.OnScrollEnd();
    }

    /**
     * Handles the touchend event for stopping dragging.
     */
    private OnTouchEnd() {
        this.removeTouchListeners();
        this.isPointerDown = false;
        this.OnScrollEnd();
    }

    /**
     * Removes mouse event listeners for dragging.
     */
    private removeDragListeners() {
        window.removeEventListener('mouseup', this.boundOnMouseUp);
        window.removeEventListener('mousemove', this.boundOnMouseMove);
    }

    /**
     * Removes touch event listeners for dragging.
     */
    private removeTouchListeners() {
        window.removeEventListener('touchend', this.boundOnTouchEnd);
        window.removeEventListener('touchmove', this.boundOnTouchMove);
    }

    /**
     * Handles the end of scrolling (either from dragging or scroll wheel).
     * Snaps to the nearest item for better UX with smooth scrolling.
     */
    private OnScrollEnd() {
        if (this.isScrolling === true) {
            return;
        }

        // If we never started dragging, do nothing:
        // this was a tap, and we want the click to fire.
        if (this.isDragging === false) {
            return;
        }

        this.isDragging = false;
        this.itemsContainerRef.classList.remove(this.draggingClass);

        setTimeout(() => {
            const firstVisibleItem = this.GetFirstVisibleItem();
            if (firstVisibleItem !== null) {
                const targetIndex = this.GetItemIndex(firstVisibleItem);
                
                // Check if the visible item is a cloned item
                if (firstVisibleItem.classList.contains(this.itemClonedClass)) {
                    // If it's a cloned item, instantly move to the non-cloned version
                    this.TransitionToNonClonedItem(targetIndex);
                } else {
                    // Regular smooth scroll to the nearest item
                    this.MoveTo(targetIndex, 'smooth');
                }
            }
        }, 50);
    }

    /**
     * Transitions from a cloned item to its non-cloned equivalent instantly
     * to maintain the infinite scrolling illusion.
     *
     * @param index - Index of the item to transition to
     */
    private TransitionToNonClonedItem(index: number) {
        const nonClonedItems = this.itemsContainerRef.querySelectorAll(this.itemWrapperSelector);
        const targetItem = nonClonedItems.item(index) as HTMLElement;
        
        if (targetItem !== null) {
            // Set scrolling flag to prevent interference
            this.isScrolling = true;
            
            // Instantly scroll to the non-cloned item
            this.itemsContainerRef.scrollTo({
                left: targetItem.offsetLeft,
                behavior: 'instant'
            });
        }

        // Clear scrolling flag immediately since it's instant
        this.isScrolling = false;
    }

    /**
     * Handles the mousemove event for updating carousel position during dragging.
     *
     * @param e - Mouse event
     */
    private OnMouseMove(e: MouseEvent) {
        if (this.isPointerDown === false) {
            return;
        }

        const dx = e.clientX - this.startClientX;

        if (this.isDragging === false) {
            if (Math.abs(dx) < this.dragThresholdPx) {
                return;
            }

            this.isDragging = true;
            this.itemsContainerRef.classList.add(this.draggingClass);
        }

        e.preventDefault();
        this.updateScrollPosition(e.pageX);
    }

    /**
     * Handles the touchmove event for updating carousel position during dragging.
     *
     * @param e - Touch event
     */
    private OnTouchMove(e: TouchEvent) {
        if (this.isPointerDown === false || e.touches.length === 0) {
            return;
        }

        const dx = e.touches[0].clientX - this.startClientX;

        if (this.isDragging === false) {
            if (Math.abs(dx) < this.dragThresholdPx) {
                return;
            }

            this.isDragging = true;
            this.itemsContainerRef.classList.add(this.draggingClass);
        }

        // Only prevent default once we've decided it's a drag,
        // otherwise taps still generate clicks.
        e.preventDefault();
        this.updateScrollPosition(e.touches[0].pageX);
    }

    /**
     * Updates the scroll position during dragging.
     *
     * @param pageX - X coordinate of the pointer
     */
    private updateScrollPosition(pageX: number) {
        const x = pageX - this.itemsContainerRef.offsetLeft;
        const walk = (x - this.startX) * 1.2; // Reduced multiplier for smoother movement
        this.itemsContainerRef.scrollLeft = this.scrollLeft - walk;

        this.ResetToNonClonedItem();
    }

    /**
     * Handles wheel events for scroll wheel detection.
     *
     * @param e - Wheel event
     */
    private OnWheel(e: WheelEvent) {
        // Clear any existing scroll timeout
        if (this.scrollTimeout !== null) {
            clearTimeout(this.scrollTimeout);
        }

        // Set a timeout to detect when scrolling has ended
        this.scrollTimeout = window.setTimeout(() => {
            this.OnScrollEnd();
            this.scrollTimeout = null;
        }, 150); // Wait 150ms after last scroll event
    }

    /**
     * Adjusts the scroll position to show the non-cloned version of the visible item.
     */
    private ResetToNonClonedItem() {
        const firstItem = this.GetFirstVisibleItem();
        if (firstItem === null || firstItem.classList.contains(this.itemClonedClass) === false) {
            return;
        }

        const nonClonedItem = this.itemsContainerRef.querySelectorAll(this.itemWrapperSelector).item(this.GetItemIndex(firstItem)) as HTMLElement;
        if (nonClonedItem !== null) {
            this.itemsContainerRef.scrollLeft += nonClonedItem.offsetLeft - firstItem.offsetLeft;
        }
    }

    /**
     * Handles resizing of the carousel by recalculating visibility of items.
     */
    private async OnResize(): Promise<void> {
        const totalWidth = Array.from(this.itemsContainerRef.querySelectorAll(this.itemWrapperSelector))
            .reduce((sum, item) => sum + (item as HTMLElement).getBoundingClientRect().width, 0);

        await this.dotnetRef.invokeMethodAsync('SetVisibility', totalWidth, this.itemsContainerRef.clientWidth);
        await this.OnScroll();

        const firstVisibleItem = this.GetFirstVisibleItem();
        if (firstVisibleItem !== null) {
            this.MoveTo(this.GetItemIndex(firstVisibleItem), 'instant');
        }
    }

    /**
     * Handles scrolling by setting the index based on the visible item with debouncing.
     */
    private async OnScroll(): Promise<void> {
        if (this.isScrolling === true) {
            return; // Don't update index during programmatic scrolling
        }

        const firstVisibleIndex = this.GetFirstVisibleItem();
        if (firstVisibleIndex !== null) {
            await this.dotnetRef.invokeMethodAsync('SetActiveIndex', this.GetItemIndex(firstVisibleIndex));
        }
    }

    /**
     * Gets the first visible item in the carousel based on scroll position.
     *
     * @returns The first visible HTML item element
     */
    private GetFirstVisibleItem(): HTMLElement | null {
        const containerRect = this.itemsContainerRef.getBoundingClientRect();

        for (let i = 0; i < this.itemsContainerRef.children.length; i++) {
            const item = this.itemsContainerRef.children[i] as HTMLElement;
            const itemRect = item.getBoundingClientRect();

            const itemVisibleWidth = Math.min(itemRect.right, containerRect.right) - Math.max(itemRect.left, containerRect.left);
            const itemWidth = itemRect.width;

            const visiblePercentage = itemVisibleWidth / itemWidth;

            if (visiblePercentage >= 0.5) {
                return item;
            }
        }

        return null;
    }

    /**
     * Gets the index of a specified item in the carousel.
     *
     * @param item - HTML element of the item
     * @returns The index of the item within non-cloned items
     */
    private GetItemIndex(item: HTMLElement): number {
        const items = Array.from(this.itemsContainerRef.children);
        const numberOfItems = this.itemsContainerRef.querySelectorAll(this.itemWrapperSelector).length;

        return items.indexOf(item) % numberOfItems;
    }

    /**
     * Disposes the carousel by removing event listeners and stopping observers.
     */
    public Dispose() {
        // Clear any pending timeouts
        if (this.scrollTimeout !== null) {
            clearTimeout(this.scrollTimeout);
        }

        // Remove resize observer
        this.resizeObserver.unobserve(this.itemsContainerRef);

        // Remove event listeners using cached bound methods
        this.itemsContainerRef.removeEventListener('mousedown', this.boundOnMouseDown);
        this.itemsContainerRef.removeEventListener('touchstart', this.boundOnTouchStart);
        this.itemsContainerRef.removeEventListener('scroll', this.boundOnScroll);
        this.itemsContainerRef.removeEventListener('wheel', this.boundOnWheel);

        // Remove any remaining window event listeners
        this.removeDragListeners();
        this.removeTouchListeners();
    }
}

/**
 * Initializes a new Carousel instance.
 *
 * @param dotNetRef - Reference to the .NET object
 * @param elementRef - Root HTML element of the carousel
 * @returns The Carousel instance
 */
window['InitCarouselV2'] = function (dotNetRef: any, elementRef: HTMLElement) {
    return new CarouselV2(dotNetRef, elementRef);
};